import React, { useState, useEffect } from 'react';
import { COMMODITIES } from './config';
import { calcIndiaLanded, fmtINR, fmtUSD, fmtChange, getCategoryStyle } from './utils';

export default function CommodityCard({ commodityKey, priceData, usdInr, onOpenChart, tickTime, lastUpdate }) {
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (lastUpdate) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [lastUpdate]);

  const config = COMMODITIES[commodityKey];
  const landed = calcIndiaLanded(config, priceData, usdInr);
  const hasData = priceData && usdInr;
  const isApprox = priceData?.isApprox || false;
  const isLME = priceData?.isLME || false;
  const change = hasData ? fmtChange(priceData.change, priceData.changePct) : fmtChange(null);

  const intlPriceStr = hasData ? fmtUSD(priceData.price) : '—';
  const intlUnit = `/ ${config.intlUnit.toLowerCase()}`;
  
  const categoryStyle = getCategoryStyle(config.category, config.categoryLabel);

  return (
    <div
      className={`commodity-card ${flash ? 'price-flash' : ''}`}
      style={{ '--commodity-accent': config.accentColor }}
    >
      <div className="commodity-card-inner">
        <div className="commodity-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              className="commodity-icon"
              style={{ background: `linear-gradient(135deg, ${config.accentBg}, ${config.accentColor})` }}
              dangerouslySetInnerHTML={{ __html: config.icon }}
            />
            <div className="commodity-info">
              <div className="commodity-name">{config.name}</div>
              <div className="commodity-symbol">{config.symbol}</div>
            </div>
          </div>
          <span className="commodity-category-badge" style={categoryStyle}>
            {config.categoryLabel}
          </span>
        </div>

        {/* International Price */}
        <div className="intl-price-row">
          <div>
            <div className="intl-price flex items-start gap-1">
              {intlPriceStr}
              {isApprox && <span style={{ fontSize: '9px', color: 'var(--orange)', fontWeight: 600, verticalAlign: 'super' }}>~APPROX</span>}
              {isLME && <span style={{ fontSize: '9px', color: 'var(--teal)', fontWeight: 600, verticalAlign: 'super' }}>LME</span>}
            </div>
            <div className="intl-unit">{intlUnit}</div>
          </div>
          <div className="intl-change relative">
            <span className={`change-pill ${change.cls}`}>{change.text}</span>
          </div>
        </div>

        {/* India Import Landed */}
        <div className="india-landed">
          <div className="india-landed-header">
            <span className="india-landed-title flex items-center gap-1">
              ₹ / {config.indiaUnit} · India Import Landed
            </span>
            <span className="india-duty-badge">{config.dutyLabel}</span>
          </div>
          <div>
            {hasData && landed ? (
              <>
                {config.showPurity ? (
                  config.purityLabels && landed.purities ? (
                    <>
                      {landed.purities.map((p, idx) => (
                        <div key={idx} className="price-row">
                          <span className="price-label">{p.label} per gram</span>
                          <span className={`price-value ${p.ratio === 1 ? 'highlight' : ''}`}>
                            {fmtINR(p.perGram)}/g
                          </span>
                        </div>
                      ))}
                      {config.showKg && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed var(--sep2)' }}>
                          {landed.purities.map((p, idx) => (
                            <div
                              key={idx}
                              className={`price-row ${p.ratio === 1 ? 'ten-gram-row' : ''}`}
                              style={{ paddingLeft: p.ratio !== 1 ? '10px' : '' }}
                            >
                              <span className="price-label" style={{ fontWeight: p.ratio === 1 ? 700 : 600 }}>
                                Per kg · {p.label}
                              </span>
                              <span className="price-value" style={{ fontWeight: p.ratio === 1 ? 800 : 700 }}>
                                {fmtINR(p.perKg, 0)}/kg
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="price-row">
                        <span className="price-label">24K per gram</span>
                        <span className="price-value highlight">{fmtINR(landed.k24)}/g</span>
                      </div>
                      <div className="price-row">
                        <span className="price-label">22K per gram</span>
                        <span className="price-value">{fmtINR(landed.k22)}/g</span>
                      </div>
                      <div className="price-row">
                        <span className="price-label">18K per gram</span>
                        <span className="price-value">{fmtINR(landed.k18)}/g</span>
                      </div>
                      {config.show10g && (
                        <>
                          <div className="price-row ten-gram-row">
                            <span className="price-label">10g · 24K</span>
                            <span className="price-value">{fmtINR(landed.per10g, 0)}</span>
                          </div>
                          <div
                            className="price-row ten-gram-row"
                            style={{ marginTop: '4px', background: 'linear-gradient(135deg, hsl(210 10% 62% / 0.06), hsl(215 18% 52% / 0.06))', borderColor: 'hsl(210 10% 62% / 0.12)' }}
                          >
                            <span className="price-label" style={{ color: 'var(--t3)' }}>10g · 22K</span>
                            <span className="price-value" style={{ color: 'var(--t2)', fontSize: '14px' }}>
                              {fmtINR(landed.per10g_22k, 0)}
                            </span>
                          </div>
                        </>
                      )}
                      {config.showKg && landed.perKg && (
                        <>
                          <div className="price-row" style={{ marginTop: '6px', paddingTop: '8px', borderTop: '1px dashed var(--sep2)' }}>
                            <span className="price-label" style={{ fontWeight: 700 }}>Per kg · 24K</span>
                            <span className="price-value" style={{ fontWeight: 800 }}>{fmtINR(landed.perKg, 0)}/kg</span>
                          </div>
                          {landed.perKg_22k && (
                            <div className="price-row">
                              <span className="price-label">Per kg · 22K</span>
                              <span className="price-value">{fmtINR(landed.perKg_22k, 0)}/kg</span>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )
                ) : (
                  <div className="price-row">
                    <span className="price-label">Per {config.indiaUnit}</span>
                    <span className="price-value highlight">{fmtINR(landed.perUnit)}/{config.indiaUnit}</span>
                  </div>
                )}

                {/* Retail Contract Equivalents */}
                {landed.minis && landed.minis.length > 0 && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed var(--sep2)' }}>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '9px', fontWeight: 700, color: 'var(--t4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                      Retail Contract Equivalents
                    </div>
                    {landed.minis.map((mc, idx) => (
                      <div key={idx} className="price-row" style={{ padding: '3px 0' }}>
                        <span className="price-label" style={{ fontSize: '11px' }}>
                          {mc.name} <span style={{ color: 'var(--t4)', fontSize: '9px' }}>({mc.lot})</span>
                        </span>
                        <span className="price-value" style={{ fontSize: '13px', color: 'var(--primary)' }}>
                          {fmtINR(mc.price, 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="price-row items-center gap-4 py-2">
                <span className="price-label">Loading...</span>
                <span className="price-value"><div className="skeleton skeleton-text" /></span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="data-source">
        <span>Source: {isLME ? 'Metals.live (LME)' : isApprox ? 'LME Approx' : `Yahoo Finance (${config.yahooSymbol || 'N/A'})`}</span>
        {(config.yahooSymbol || config.lmeOnly) && (
          <button
            className="chart-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChart(commodityKey);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg> Chart
          </button>
        )}
        <span>{tickTime}</span>
      </div>
    </div>
  );
}
